import { useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Download } from 'lucide-react';

export interface LightboxImage {
  url: string;
  label?: string;
  type?: 'image' | 'video';
}

interface Props {
  images: LightboxImage[];
  index: number;
  onClose: () => void;
  onNavigate?: (index: number) => void;
  allowDownload?: boolean;
}

export default function Lightbox({ images, index, onClose, onNavigate, allowDownload = true }: Props) {
  const count = images.length;
  const current = images[index];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && onNavigate) onNavigate((index - 1 + count) % count);
      if (e.key === 'ArrowRight' && onNavigate) onNavigate((index + 1) % count);
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [index, count, onClose, onNavigate]);

  if (!current) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <button
        className="absolute top-4 right-4 text-white/80 hover:text-white p-2 rounded-full bg-white/10"
        onClick={onClose}
        aria-label="Close"
      >
        <X className="h-6 w-6" />
      </button>

      {count > 1 && (
        <>
          <button
            className="absolute left-2 sm:left-4 text-white/80 hover:text-white p-2 rounded-full bg-white/10"
            onClick={(e) => { e.stopPropagation(); onNavigate?.((index - 1 + count) % count); }}
            aria-label="Previous image"
          >
            <ChevronLeft className="h-8 w-8" />
          </button>
          <button
            className="absolute right-2 sm:right-4 text-white/80 hover:text-white p-2 rounded-full bg-white/10"
            onClick={(e) => { e.stopPropagation(); onNavigate?.((index + 1) % count); }}
            aria-label="Next image"
          >
            <ChevronRight className="h-8 w-8" />
          </button>
        </>
      )}

      <div className="flex flex-col items-center max-w-full max-h-full" onClick={(e) => e.stopPropagation()}>
        {current.type === 'video' ? (
          <video
            key={current.url}
            src={current.url}
            controls
            autoPlay
            playsInline
            className="max-h-[85vh] max-w-[92vw] object-contain bg-black"
          />
        ) : (
          <img
            src={current.url}
            alt={current.label || 'Preview'}
            className="max-h-[85vh] max-w-[92vw] object-contain"
          />
        )}
        <div className="flex items-center gap-3 mt-3 text-white/80 text-sm">
          {count > 1 && <span>{index + 1} / {count}</span>}
          {allowDownload && current.type !== 'video' && (
            <a
              href={current.url}
              download
              className="flex items-center gap-1 hover:text-white px-3 py-1.5 rounded-lg bg-white/10"
            >
              <Download className="h-4 w-4" /> Download
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
